/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 *
 * Eigenvalues of a dense real-symmetric matrix held in a flat row-major
 * Float64Array — Householder tridiagonalization followed by implicit-shift QL,
 * eigenvalues only. The same algorithm as symmetricEigenvaluesQL in
 * semiconductor/quantum/hermitianEigen.ts, rewritten for typed arrays and
 * contiguous memory access: a finite nanostructure's tight-binding matrix is
 * 5 × (atoms) wide (1,000–2,500), where the array-of-arrays version takes
 * 3–19 s and column-strided reads dominate. Here the full symmetric matrix is
 * kept up to date during the reduction so every inner loop walks one row.
 * Validated against symmetricEigenvaluesQL in nanostructures.test.ts.
 *
 * The matrix is destroyed. Returns the eigenvalues ascending.
 */

export const symmetricEigenvaluesFlat = (a: Float64Array, n: number, maxIter = 60): Float64Array => {
  if (n === 0) return new Float64Array(0);
  if (n === 1) return Float64Array.of(a[0]);
  if (a.length < n * n) throw new Error('symmetricEigenvaluesFlat: matrix too small');
  const d = new Float64Array(n);
  const e = new Float64Array(n);

  // ── Householder reduction to tridiagonal form ──
  // Row i of the symmetric matrix is a[i*n .. i*n+n). At step i we eliminate
  // row/column i below the sub-diagonal using the Householder vector u held
  // in row i (entries 0..l), with p = A·u/h in e[0..l].
  for (let i = n - 1; i >= 1; i--) {
    const l = i - 1;
    const ri = i * n;
    let h = 0;
    let scale = 0;
    if (l > 0) {
      for (let k = 0; k <= l; k++) scale += Math.abs(a[ri + k]);
      if (scale === 0) {
        e[i] = a[ri + l];
      } else {
        const inv = 1 / scale;
        for (let k = 0; k <= l; k++) {
          const v = a[ri + k] * inv;
          a[ri + k] = v;
          h += v * v;
        }
        let f = a[ri + l];
        const g = f >= 0 ? -Math.sqrt(h) : Math.sqrt(h);
        e[i] = scale * g;
        h -= f * g;
        a[ri + l] = f - g;
        f = 0;
        const invH = 1 / h;
        // p_j = (A u)_j / h over the leading (l+1)×(l+1) block — contiguous row j.
        for (let j = 0; j <= l; j++) {
          const rj = j * n;
          let gj = 0;
          for (let k = 0; k <= l; k++) gj += a[rj + k] * a[ri + k];
          gj *= invH;
          e[j] = gj;
          f += gj * a[ri + j];
        }
        const hh = f / (h + h);
        // q = p − (u·p / 2h) u, then A ← A − u qᵀ − q uᵀ on the full block
        // (both triangles, so later row reads stay contiguous).
        for (let j = 0; j <= l; j++) e[j] -= hh * a[ri + j];
        for (let j = 0; j <= l; j++) {
          const rj = j * n;
          const uj = a[ri + j];
          const qj = e[j];
          for (let k = 0; k <= l; k++) a[rj + k] -= uj * e[k] + qj * a[ri + k];
        }
      }
    } else {
      e[i] = a[ri + l];
    }
    d[i] = h;
  }
  for (let i = 0; i < n; i++) d[i] = a[i * n + i];

  // ── QL with implicit shifts on the tridiagonal (d, e) ──
  for (let i = 1; i < n; i++) e[i - 1] = e[i];
  e[n - 1] = 0;
  const sign = (x: number, y: number) => (y >= 0 ? Math.abs(x) : -Math.abs(x));

  for (let l = 0; l < n; l++) {
    let iter = 0;
    let m = l;
    do {
      for (m = l; m < n - 1; m++) {
        const dd = Math.abs(d[m]) + Math.abs(d[m + 1]);
        if (Math.abs(e[m]) <= Number.EPSILON * dd) break;
      }
      if (m !== l) {
        // Numerical Recipes' tqli throws here; returning partly converged
        // values silently gave a wrong gap with no sign of it.
        if (iter++ === maxIter) throw new Error(`eigen_not_converged: ${maxIter} QL iterations at row ${l} of ${n}`);
        let g = (d[l + 1] - d[l]) / (2 * e[l]);
        let r = Math.hypot(g, 1);
        g = d[m] - d[l] + e[l] / (g + sign(r, g));
        let s = 1;
        let c = 1;
        let p = 0;
        let brokeZero = false;
        for (let i = m - 1; i >= l; i--) {
          const f = s * e[i];
          const b = c * e[i];
          r = Math.hypot(f, g);
          e[i + 1] = r;
          if (r === 0) {
            d[i + 1] -= p;
            e[m] = 0;
            brokeZero = true;
            break;
          }
          s = f / r;
          c = g / r;
          g = d[i + 1] - p;
          r = (d[i] - g) * s + 2 * c * b;
          p = s * r;
          d[i + 1] = g + p;
          g = c * r - b;
        }
        if (brokeZero) continue;
        d[l] -= p;
        e[l] = g;
        e[m] = 0;
      }
    } while (m !== l);
  }

  return d.sort();
};

/**
 * Eigenvalues of a complex Hermitian matrix H = A + iB given as two flat n×n
 * arrays, via the 2n×2n real-symmetric embedding [[A, −B], [B, A]]. Every
 * eigenvalue appears twice; the distinct ones (every other entry of the
 * sorted result) are returned.
 */
export const hermitianEigenvaluesFlat = (re: Float64Array, im: Float64Array, n: number): Float64Array => {
  const N = 2 * n;
  const m = new Float64Array(N * N);
  for (let i = 0; i < n; i++) {
    for (let j = 0; j < n; j++) {
      const aij = re[i * n + j];
      const bij = im[i * n + j];
      m[i * N + j] = aij;
      m[(i + n) * N + (j + n)] = aij;
      m[i * N + (j + n)] = -bij;
      m[(i + n) * N + j] = bij;
    }
  }
  const all = symmetricEigenvaluesFlat(m, N);
  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = all[2 * i];
  return out;
};

/**
 * Full eigendecomposition (values + vectors) of a dense real-symmetric matrix
 * in a flat row-major Float64Array: Householder reduction to tridiagonal form
 * with the transformation accumulated, then implicit-shift QL rotating the
 * accumulated matrix (Numerical Recipes tred2 + tqli). O(n³) with vectors, a
 * few hundred milliseconds at n = 500 — the cyclic Jacobi routine in
 * hermitianEigen.ts takes half a minute there, which is why the finite
 * π-model segment (≤ 500 carbons) uses this. The matrix is destroyed.
 *
 * Returns the eigenvalues ascending and `vectors`, where vectors[j] (a view of
 * length n) is the normalized eigenvector of values[j]. Validated against the
 * Jacobi routine and by residuals ‖H v − λ v‖ in nanostructures.test.ts.
 */
export const symmetricEigenDecompositionFlat = (
  a: Float64Array,
  n: number,
  maxIter = 60,
): { values: Float64Array; vectors: Float64Array[] } => {
  if (n === 0) return { values: new Float64Array(0), vectors: [] };
  if (n === 1) return { values: Float64Array.of(a[0]), vectors: [Float64Array.of(1)] };
  const d = new Float64Array(n);
  const e = new Float64Array(n);
  const at = (i: number, j: number) => i * n + j;

  // ── tred2: Householder reduction, accumulating the transformation in `a` ──
  for (let i = n - 1; i >= 1; i--) {
    const l = i - 1;
    let h = 0;
    let scale = 0;
    if (l > 0) {
      for (let k = 0; k <= l; k++) scale += Math.abs(a[at(i, k)]);
      if (scale === 0) {
        e[i] = a[at(i, l)];
      } else {
        for (let k = 0; k <= l; k++) {
          a[at(i, k)] /= scale;
          h += a[at(i, k)] * a[at(i, k)];
        }
        let f = a[at(i, l)];
        const g = f >= 0 ? -Math.sqrt(h) : Math.sqrt(h);
        e[i] = scale * g;
        h -= f * g;
        a[at(i, l)] = f - g;
        f = 0;
        for (let j = 0; j <= l; j++) {
          a[at(j, i)] = a[at(i, j)] / h;
          let gj = 0;
          for (let k = 0; k <= j; k++) gj += a[at(j, k)] * a[at(i, k)];
          for (let k = j + 1; k <= l; k++) gj += a[at(k, j)] * a[at(i, k)];
          e[j] = gj / h;
          f += e[j] * a[at(i, j)];
        }
        const hh = f / (h + h);
        for (let j = 0; j <= l; j++) {
          const fj = a[at(i, j)];
          const gj = e[j] - hh * fj;
          e[j] = gj;
          for (let k = 0; k <= j; k++) a[at(j, k)] -= fj * e[k] + gj * a[at(i, k)];
        }
      }
    } else {
      e[i] = a[at(i, l)];
    }
    d[i] = h;
  }
  d[0] = 0;
  e[0] = 0;
  for (let i = 0; i < n; i++) {
    const l = i - 1;
    if (d[i] !== 0) {
      for (let j = 0; j <= l; j++) {
        let g = 0;
        for (let k = 0; k <= l; k++) g += a[at(i, k)] * a[at(k, j)];
        for (let k = 0; k <= l; k++) a[at(k, j)] -= g * a[at(k, i)];
      }
    }
    d[i] = a[at(i, i)];
    a[at(i, i)] = 1;
    for (let j = 0; j <= l; j++) {
      a[at(j, i)] = 0;
      a[at(i, j)] = 0;
    }
  }

  // Transpose the accumulated matrix so eigenvector j is the contiguous row j
  // (the QL rotations below then walk rows, not strided columns).
  const z = new Float64Array(n * n);
  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) z[at(j, i)] = a[at(i, j)];

  // ── tqli: implicit-shift QL on (d, e), rotating z ──
  for (let i = 1; i < n; i++) e[i - 1] = e[i];
  e[n - 1] = 0;
  const sign = (x: number, y: number) => (y >= 0 ? Math.abs(x) : -Math.abs(x));
  for (let l = 0; l < n; l++) {
    let iter = 0;
    let m = l;
    do {
      for (m = l; m < n - 1; m++) {
        const dd = Math.abs(d[m]) + Math.abs(d[m + 1]);
        if (Math.abs(e[m]) <= Number.EPSILON * dd) break;
      }
      if (m !== l) {
        // Numerical Recipes' tqli throws here; returning partly converged
        // values silently gave a wrong gap with no sign of it.
        if (iter++ === maxIter) throw new Error(`eigen_not_converged: ${maxIter} QL iterations at row ${l} of ${n}`);
        let g = (d[l + 1] - d[l]) / (2 * e[l]);
        let r = Math.hypot(g, 1);
        g = d[m] - d[l] + e[l] / (g + sign(r, g));
        let s = 1;
        let c = 1;
        let p = 0;
        let brokeZero = false;
        for (let i = m - 1; i >= l; i--) {
          let f = s * e[i];
          const b = c * e[i];
          r = Math.hypot(f, g);
          e[i + 1] = r;
          if (r === 0) {
            d[i + 1] -= p;
            e[m] = 0;
            brokeZero = true;
            break;
          }
          s = f / r;
          c = g / r;
          g = d[i + 1] - p;
          r = (d[i] - g) * s + 2 * c * b;
          p = s * r;
          d[i + 1] = g + p;
          g = c * r - b;
          const r0 = i * n;
          const r1 = (i + 1) * n;
          for (let k = 0; k < n; k++) {
            f = z[r1 + k];
            z[r1 + k] = s * z[r0 + k] + c * f;
            z[r0 + k] = c * z[r0 + k] - s * f;
          }
        }
        if (brokeZero) continue;
        d[l] -= p;
        e[l] = g;
        e[m] = 0;
      }
    } while (m !== l);
  }

  // Sort ascending, carrying the vectors.
  const order = Array.from({ length: n }, (_, i) => i).sort((p, q) => d[p] - d[q]);
  const values = new Float64Array(n);
  const vectors: Float64Array[] = new Array(n);
  for (let j = 0; j < n; j++) {
    values[j] = d[order[j]];
    vectors[j] = z.subarray(order[j] * n, order[j] * n + n);
  }
  return { values, vectors };
};
